import React from 'react';
import {
  Box, Paper, Typography, Grid, TextField, MenuItem, Button, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Chip
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  DeleteOutline as DeleteIcon,
  Receipt as ReceiptIcon,
  Payments as PaymentsIcon,
  AddCard as AddCardIcon
} from '@mui/icons-material';

/**
 * Paper "Abonos y Pagos Recibidos": formulario de registro de abono
 * (o carga de DTE) y tabla de abonos ya registrados. Solo se muestra
 * para albaranes ya existentes.
 */
const AlbaranAbonosPanel = ({
  esNuevo,
  esCerrado,
  albaran,
  esEditable,
  formasPago,
  idFormaPago,
  setIdFormaPago,
  montoAbono,
  setMontoAbono,
  referenciaAbono,
  setReferenciaAbono,
  observacionesAbono,
  setObservacionesAbono,
  onRegistrarAbono,
  onAbrirImportDte,
  abonos,
  onAnularAbono,
}) => {
  const theme = useTheme();

  if (esNuevo) return null;

  const mostrarFormularioAbono = albaran?.estado_albaran === 'ABIERTO' || esCerrado;

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        borderRadius: 3,
        overflow: 'hidden',
        border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
        boxShadow: '0 4px 20px 0 rgba(0,0,0,0.03)',
      }}
    >
      {/* Header Unificado */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justify: 'space-between',
          px: 2.5,
          py: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.common.black, 0.01),
          flexWrap: 'wrap',
          gap: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              p: 0.8,
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette.success.main, 0.08),
              color: 'success.main',
              display: 'flex',
            }}
          >
            <PaymentsIcon fontSize="small" />
          </Box>
          <Typography variant="subtitle1" fontWeight={700}>
            Abonos y Pagos Recibidos
          </Typography>
        </Box>

        <Chip
          size="small"
          label={`${abonos.length} ${abonos.length === 1 ? 'Abono registrado' : 'Abonos registrados'}`}
          color={abonos.length > 0 ? 'success' : 'default'}
          variant="outlined"
          sx={{ fontWeight: 600 }}
        />
      </Box>

      {/* Contenido / Formulario y Tabla */}
      <Box sx={{ p: 2.5 }}>
        {mostrarFormularioAbono && (
          <Box
            sx={{
              mb: 3,
              p: 2.5,
              borderRadius: 2.5,
              bgcolor: alpha(theme.palette.primary.main, 0.02),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight={700}
              color="primary.main"
              sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <AddCardIcon fontSize="small" /> Registrar Nuevo Pago / Abono
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Forma de Pago"
                  value={
                    formasPago.some((fp) => String(fp.id_forma_pago) === String(idFormaPago))
                      ? idFormaPago
                      : ''
                  }
                  onChange={(e) => setIdFormaPago(e.target.value)}
                >
                  {formasPago.map((fp) => (
                    <MenuItem key={fp.id_forma_pago} value={fp.id_forma_pago}>
                      {fp.desc_forma_pago}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  size="small"
                  label="Monto a Abonar"
                  value={montoAbono}
                  onChange={(e) => setMontoAbono(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Referencia (Nº Voucher / Transacción)"
                  value={referenciaAbono}
                  onChange={(e) => setReferenciaAbono(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Observaciones"
                  value={observacionesAbono}
                  onChange={(e) => setObservacionesAbono(e.target.value)}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, pt: 0.5 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={onRegistrarAbono}
                    disabled={!idFormaPago || !montoAbono}
                    sx={{
                      py: 1,
                      borderRadius: 2,
                      fontWeight: 700,
                      textTransform: 'none',
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.25)}`,
                    }}
                  >
                    Registrar Abono
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    color="secondary"
                    startIcon={<ReceiptIcon />}
                    onClick={onAbrirImportDte}
                    disabled={!montoAbono || parseFloat(montoAbono) <= 0}
                    sx={{
                      py: 1,
                      borderRadius: 2,
                      fontWeight: 700,
                      textTransform: 'none',
                    }}
                  >
                    Cargar JSON DTE
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Tabla de Abonos con Scroll Horizontal */}
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            width: '100%',
            maxWidth: '100%',
            overflowX: 'auto', // Permite desplazamiento horizontal
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
          }}
        >
          <Table size="small" sx={{ minWidth: 600 }}> {/* Forzamos un ancho mínimo para activar el scroll si la pantalla se comprime */}
            <TableHead sx={{ bgcolor: alpha(theme.palette.common.black, 0.02) }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Fecha</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Monto</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Referencia</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Observaciones</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Forma de Pago</TableCell>
                {esEditable && <TableCell align="center" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Acción</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {abonos.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={esEditable ? 6 : 5}
                    align="center"
                    sx={{ py: 3, color: 'text.secondary' }}
                  >
                    No hay abonos registrados para este albarán.
                  </TableCell>
                </TableRow>
              ) : (
                abonos.map((ab) => {
                  const fpAsociada = formasPago.find((f) => f.id_forma_pago === ab.id_forma_pago);
                  return (
                    <TableRow
                      key={ab.id_albaran_abono || ab.id_abono}
                      hover
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {ab.createdAt
                          ? new Date(ab.createdAt).toLocaleDateString('es-ES')
                          : ab.fecha_abono || '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'success.main', whiteSpace: 'nowrap' }}>
                        ${parseFloat(ab.monto_abono || ab.monto || 0).toFixed(2)}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{ab.referencia_abono || '-'}</TableCell>
                      <TableCell>{ab.observaciones || '-'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {fpAsociada ? fpAsociada.desc_forma_pago : ab.forma_pago_nombre || ab.id_forma_pago}
                      </TableCell>

                      {esEditable && (
                        <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => onAnularAbono(ab)}
                            sx={{
                              bgcolor: alpha(theme.palette.error.main, 0.04),
                              '&:hover': {
                                bgcolor: alpha(theme.palette.error.main, 0.12),
                              },
                            }}
                            title="Anular / Eliminar Abono"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Paper>
  );
};

export default AlbaranAbonosPanel;